import { RotateCcw } from "lucide-react";
import type { CSSProperties } from "react";
import { useRouter } from "../../store/router";
import { useBoothVideoEnabled, useBoothPayment } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { MOTION_PICK_MIN } from "@fiket/booth-schema";
import { useSelectedStyle, useOutputAspect, useMotionsForStyle } from "../../hooks/useStyles";
import { Button } from "../../components/ui/Button";
import { videoEntryRoute } from "../Video/videoFlow";
import { receiveRoute } from "../Payment/paymentFlow";
import { formatKrw } from "../Payment/paymentViewLogic";

/**
 * STAGE · Review (HERIT 04b · BOOTH-54) — 결과 사진을 플랫하게(테두리 없음, r8) 크게 보여주고
 * 아웃라인 "다시 찍기" + 그린 "사진 받기". 영상 지원 스타일이면 텍스트 버튼으로 영상 만들기.
 * 변환 실패로 그대로 진행한 경우 원본 사진을 보여준다.
 */
export function ReviewView() {
  const go = useRouter((s) => s.go);
  const shot = useRouter((s) => s.capturedShots[0] ?? null);
  const clearShots = useRouter((s) => s.clearShots);
  const clearVideo = useRouter((s) => s.clearVideo);
  const clearMotions = useRouter((s) => s.clearMotions);
  const clearVideoCaption = useRouter((s) => s.clearVideoCaption);
  const beginPayment = useRouter((s) => s.beginPayment);
  const paymentExit = useRouter((s) => s.paymentExit);
  const clearPaymentExit = useRouter((s) => s.clearPaymentExit);
  const pay = useBoothPayment();
  const { t, language } = useI18n();
  const style = useSelectedStyle();
  const aspect = useOutputAspect();
  const motions = useMotionsForStyle(style);

  // 부스 스위치(콘솔 부스 관리 "영상")가 꺼져 있으면 스타일에 영상 프롬프트가 있어도 숨긴다 (BOOTH-60).
  // 영상 결제 ON 인데 영상 가격이 없으면(F1 fail-closed) 영상 CTA 를 숨긴다 (BOOTH-61/80). 사진 결제는 따로 — 기본 OFF.
  // 서버는 기본 프롬프트가 없어도 동작(1~2개)만으로 합성하므로(composeVideoPrompt) 프롬프트 또는 동작 둘 중 하나면 된다.
  const boothVideoEnabled = useBoothVideoEnabled();
  const videoEnabled =
    boothVideoEnabled && !pay.videoBlocked && Boolean(style?.videoEnabled) &&
    (Boolean(style?.videoPrompt) || motions.length >= MOTION_PICK_MIN);
  // 영상 결제가 걸리면 영상 CTA 에 가격을 붙인다 (캔버스 9 "움직이는 화보 +5,000원")
  const videoLabel = pay.videoKrw !== null ? t.review.makeVideoPriced(formatKrw(pay.videoKrw, language)) : t.review.makeVideo;

  const displayUrl = shot?.transformedDataUrl ?? shot?.dataUrl ?? null;
  const isError = shot?.transformStatus === "error";

  // 결제 취소·만료 안내는 손님이 직접 행동할 때 지운다.
  // unmount cleanup 으로 지우면 StrictMode 의 mount→cleanup→mount 이중 실행에서 즉시 사라지고,
  // 다른 이유로 리마운트될 때도 깨진다 — "행동 = 지움" 이 유일하게 안전하다.
  const retake = () => {
    clearPaymentExit();
    clearShots();
    clearVideo();
    clearMotions();
    clearVideoCaption();
    go("preview");
  };

  // 항상 소개(9.1) → 고를 동작이 있으면 동작 선택(9.2, 1~2개) → 없으면 바로 생성 (videoFlow.ts)
  const makeVideo = () => {
    clearPaymentExit();
    clearVideo();
    clearMotions();
    go(videoEntryRoute());
  };

  // 사진 받기 — 사진 결제가 걸린 부스만 결제(사진 1행) 먼저. 기본(사진 OFF)은 바로 사진 받기 화면.
  // payment 로 갈 때는 beginPayment 내부에서 paymentExit 를 초기화하므로 여기서 따로 지우지 않는다.
  const receive = () => {
    const route = receiveRoute(pay.photoActive);
    if (route === "payment") {
      beginPayment(false);
    } else {
      clearPaymentExit();
    }
    go(route);
  };

  const arStyle = { "--ar": String(aspect.w / aspect.h), "--ar-css": `${aspect.w} / ${aspect.h}` } as CSSProperties;

  return (
    <div className="view view--review">
      {/* 결과 화면에도 단계 제목 — 카탈로그 review.title 은 있었으나 쓰이지 않았다 (2026-10-21) */}
      <h1 className="view__title">{t.review.title}</h1>
      <div className="view__body">
        <div className="photo-wrap">
          {displayUrl ? <img className="photo" src={displayUrl} alt="" draggable={false} style={arStyle} /> : null}
          {isError ? <span className="chip photo__badge">{t.review.slotError}</span> : null}
        </div>
      </div>
      {/* 폰에서는 .actions 가 스크롤 하단에 고정된다 */}
      <div className="view__cta actions">
        <Button variant="secondary" onClick={retake}><RotateCcw size={22} aria-hidden />{t.review.retake}</Button>
        <Button onClick={receive}>{t.review.next}</Button>
      </div>
      {/* 캔버스 9: 주 CTA 아래 전폭 보조 버튼 "움직이는 화보로 만들기" */}
      {videoEnabled && shot?.transformedDataUrl ? (
        <div className="view__sub view__sub--video">
          <Button variant="secondary" wide onClick={makeVideo}>
            {videoLabel}
          </Button>
        </div>
      ) : null}
      {paymentExit ? (
        <div className="view__sub"><p className="note" role="status">{paymentExit === "expired" ? t.payment.expired : t.payment.closed}</p></div>
      ) : null}
    </div>
  );
}
